import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/shared/components/error-state';

import { BrandForm } from './brand-form';
import { useBrandDetails, useCreateBrand, useUpdateBrand } from '../api/brands.queries';
import { emptyToNull, type BrandFormValues } from '../schemas/brand.schema';
import type { BrandHeader, UpdateBrandPayload } from '../types/brand.types';

/**
 * Dialog wrapper that owns the create/update mutations.
 *
 * Separating this from `BrandForm` keeps the form a pure, testable input
 * component — it takes values and reports a submit, and knows nothing about
 * queries — while this component handles the data-layer concerns.
 */
export function BrandFormDialog({
  open,
  onOpenChange,
  brand,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit for create mode. */
  brand?: BrandHeader;
}) {
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();

  const mutation = brand ? updateBrand : createBrand;
  const isEdit = brand !== undefined;

  const handleSubmit = (values: BrandFormValues) => {
    // The form models optional text as possibly-empty strings so that the Zod
    // schema's input and output types stay identical. The API expects null for
    // "unset", so the conversion happens once, here.
    const payload: UpdateBrandPayload = {
      name: values.name.trim(),
      arName: values.arName.trim(),
      categoryId: values.categoryId,
      slug: emptyToNull(values.slug),
      description: emptyToNull(values.description),
      imageUrl: emptyToNull(values.imageUrl),
    };

    if (brand) {
      // Status is not part of an edit: it changes through its own confirmed action.
      updateBrand.mutate({ brandId: brand.id, payload }, { onSuccess: () => onOpenChange(false) });
    } else {
      createBrand.mutate(
        { ...payload, isActive: values.isActive },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isEdit ? `Edit ${brand.name}` : 'Add brand'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? 'Update this brand’s catalogue details.'
              : 'Create a new merchant brand in the Digital Codex catalogue.'}
          </DialogDescription>
        </DialogHeader>

        {/* `key` remounts the form when switching between brands, so the
            defaults are re-read instead of showing the previous brand's
            values in a stale form state. */}
        <BrandForm
          key={brand?.id ?? 'create'}
          brand={brand}
          isPending={mutation.isPending}
          error={mutation.error}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Edit dialog opened from the brand list, whose rows do not carry every
 * editable field. Loads the brand first (sharing the Brand Details cache),
 * so the form never starts from partial values.
 */
export function BrandEditDialog({
  brandId,
  onClose,
}: {
  /** Null closes the dialog. */
  brandId: number | null;
  onClose: () => void;
}) {
  const details = useBrandDetails(brandId ?? undefined);

  // Wait for fresh, settled data before showing the form. The brand-detail
  // query is invalidated after an edit, but while the dialog is closed it is
  // inactive, so it is not refetched until reopened. Rendering from the stale
  // cache here would feed the previous values into the form's `defaultValues`
  // (read once at mount), and the later refetch would not reset them — so the
  // reopened form would show the pre-edit category. Gating on `!isFetching`
  // shows the loading state until the refetch lands with the current values.
  if (brandId !== null && details.data && !details.isFetching) {
    return (
      <BrandFormDialog
        open
        onOpenChange={(open) => !open && onClose()}
        brand={details.data.brand}
      />
    );
  }

  return (
    <Dialog open={brandId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Edit brand</DialogTitle>
          <DialogDescription className="text-xs">Loading the brand’s details…</DialogDescription>
        </DialogHeader>
        {details.error ? (
          <ErrorState error={details.error} onRetry={() => void details.refetch()} />
        ) : (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-2/3" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
